// Collections: assets that are lists — curated entries, or what a set of rules selects. A
// collection's name, description and handle are the asset's, written through `updateAsset`.

import { api } from '../client'
import type { components } from '../schema'

export type CollectionView = components['schemas']['CollectionView']
export type CollectionItemEntry = components['schemas']['CollectionItemEntry']
export type CollectionSubtype = components['schemas']['CollectionSubtype']
export type CollectionOrderBy = components['schemas']['CollectionOrderBy']
export type CollectionOrderDirection = components['schemas']['CollectionOrderDirection']
export type CollectionSmartRules = components['schemas']['CollectionSmartRules']
export type PopulationMode = components['schemas']['PopulationMode']
export type CreateCollectionRequest = components['schemas']['CreateCollectionRequest']
export type UpdateCollectionRequest = components['schemas']['UpdateCollectionRequest']
export type AddCollectionItemsRequest = components['schemas']['AddCollectionItemsRequest']
export type CollectionItemsAdded = components['schemas']['CollectionItemsAdded']
export type ReorderCollectionItemsRequest = components['schemas']['ReorderCollectionItemsRequest']
export type CollectionItemsArranged = components['schemas']['CollectionItemsArranged']
export type UpdateCollectionItemRequest = components['schemas']['UpdateCollectionItemRequest']
export type PreviewCollectionRequest = components['schemas']['PreviewCollectionRequest']
export type CollectionPreview = components['schemas']['CollectionPreview']

function collectionPath(collectionId: string): string {
  return `/collections/${encodeURIComponent(collectionId)}`
}

/** Make a collection on a workspace's shelf. 409 when its handle is already the organization's. */
export function createCollection(request: CreateCollectionRequest): Promise<CollectionView> {
  return api.post<CollectionView>('/collections', request)
}

/**
 * One collection, whole: the asset's record and the list's columns. `item_count` is zero on a
 * smart collection, which holds no entries — what it resolves to is its items.
 */
export function getCollection(collectionId: string, signal?: AbortSignal): Promise<CollectionView> {
  return api.get<CollectionView>(collectionPath(collectionId), undefined, signal)
}

/** Put a collection in the trash. The assets in it are not touched. */
export function deleteCollection(collectionId: string): Promise<void> {
  return api.del<void>(collectionPath(collectionId))
}

/** Change what kind of list a collection is and how it is ordered — mode, order and rules in one. */
export function updateCollection(collectionId: string, request: UpdateCollectionRequest): Promise<CollectionView> {
  return api.patch<CollectionView>(collectionPath(collectionId), request)
}

/**
 * What is in a collection, in its own order, at most 500: a curated one's entries, or what a smart
 * one's rules select now (`item_id` is `null` there). Only what the caller may read.
 */
export function listCollectionItems(collectionId: string, signal?: AbortSignal): Promise<CollectionItemEntry[]> {
  return api.get<CollectionItemEntry[]>(`${collectionPath(collectionId)}/items`, undefined, signal)
}

/**
 * Put assets — named, or a folder's contents — in a curated collection, and say what was left out
 * and why.
 */
export function addCollectionItems(
  collectionId: string,
  request: AddCollectionItemsRequest,
): Promise<CollectionItemsAdded> {
  return api.post<CollectionItemsAdded>(`${collectionPath(collectionId)}/items`, request)
}

/**
 * Arrange a collection's entries. Read only while `is_manually_ordered` is on — set it with
 * `updateCollection`; an id that is no longer an entry is left alone.
 */
export function reorderCollectionItems(
  collectionId: string,
  request: ReorderCollectionItemsRequest,
): Promise<CollectionItemsArranged> {
  return api.put<CollectionItemsArranged>(`${collectionPath(collectionId)}/items/order`, request)
}

/** Take one entry out of a collection. */
export function removeCollectionItem(collectionId: string, itemId: string): Promise<void> {
  return api.del<void>(`${collectionPath(collectionId)}/items/${encodeURIComponent(itemId)}`)
}

/** Retitle or trim one entry. */
export function updateCollectionItem(
  collectionId: string,
  itemId: string,
  request: UpdateCollectionItemRequest,
): Promise<void> {
  return api.patch<void>(`${collectionPath(collectionId)}/items/${encodeURIComponent(itemId)}`, request)
}

/** What a set of rules would select and how many that is in all, without storing them. */
export function previewCollection(
  collectionId: string,
  request: PreviewCollectionRequest,
  signal?: AbortSignal,
): Promise<CollectionPreview> {
  return api.post<CollectionPreview>(`${collectionPath(collectionId)}/preview`, request, undefined, signal)
}
